import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { ConfidenceTrajectoryPointDto } from '@common/synapse/introspection-types';

const CHART_WIDTH = 420;
const CHART_HEIGHT = 160;
const PADDING = { top: 12, right: 16, bottom: 28, left: 36 };

const KIND_COLORS: Record<string, string> = {
  created: '#2e7d32',
  reinforced: '#1565c0',
  weakened: '#ed6c02',
  adjusted: '#9c27b0',
  contradiction: '#d32f2f',
  edited: '#f9a825',
  retracted: '#757575',
  current: '#0288d1',
};

export interface ConfidenceTrajectoryChartProps {
  points: ConfidenceTrajectoryPointDto[];
}

function formatTurnLabel(point: ConfidenceTrajectoryPointDto, index: number): string {
  if (point.turnIndex != null) return `T${point.turnIndex}`;
  return `#${index + 1}`;
}

const ConfidenceTrajectoryChart = ({ points }: ConfidenceTrajectoryChartProps) => {
  const theme = useTheme();

  const chart = useMemo(() => {
    if (points.length === 0) return null;

    const innerW = CHART_WIDTH - PADDING.left - PADDING.right;
    const innerH = CHART_HEIGHT - PADDING.top - PADDING.bottom;
    const xStep = points.length > 1 ? innerW / (points.length - 1) : 0;

    const coords = points.map((point, index) => ({
      point,
      x: PADDING.left + (points.length > 1 ? index * xStep : innerW / 2),
      y: PADDING.top + innerH * (1 - point.confidence),
    }));

    const polyline = coords.map((c) => `${c.x},${c.y}`).join(' ');

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((value) => ({
      value,
      y: PADDING.top + innerH * (1 - value),
    }));

    return { coords, polyline, yTicks };
  }, [points]);

  if (!chart || points.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No confidence history recorded for this claim yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: 'auto' }}>
        <svg
          width={CHART_WIDTH}
          height={CHART_HEIGHT}
          role="img"
          aria-label="Confidence trajectory chart"
          style={{ display: 'block' }}
        >
          {chart.yTicks.map((tick) => (
            <g key={tick.value}>
              <line
                x1={PADDING.left}
                y1={tick.y}
                x2={CHART_WIDTH - PADDING.right}
                y2={tick.y}
                stroke={theme.palette.divider}
                strokeDasharray="3 3"
              />
              <text
                x={PADDING.left - 6}
                y={tick.y + 4}
                textAnchor="end"
                fontSize={10}
                fill={theme.palette.text.secondary}
              >
                {tick.value.toFixed(2)}
              </text>
            </g>
          ))}

          <polyline
            fill="none"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            points={chart.polyline}
          />

          {chart.coords.map(({ point, x, y }, index) => (
            <g key={`${point.timestamp}-${index}`}>
              <circle
                cx={x}
                cy={y}
                r={4}
                fill={KIND_COLORS[point.kind] ?? theme.palette.primary.main}
                stroke={theme.palette.background.paper}
                strokeWidth={1.5}
              />
              <text
                x={x}
                y={CHART_HEIGHT - 6}
                textAnchor="middle"
                fontSize={9}
                fill={theme.palette.text.secondary}
              >
                {formatTurnLabel(point, index)}
              </text>
            </g>
          ))}
        </svg>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {points.map((point, index) => (
          <Typography key={`${point.timestamp}-${index}`} variant="caption" color="text.secondary">
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: KIND_COLORS[point.kind] ?? theme.palette.primary.main,
                mr: 0.5,
                verticalAlign: 'middle',
              }}
            />
            {point.label}
            {point.confidence != null ? ` (${point.confidence.toFixed(2)})` : ''}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default ConfidenceTrajectoryChart;
